import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { createImageVariantConfig } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { getFavoriteIds } from '../../util/favorites';
import { fetchCurrentUser, toggleFavoriteListingThunk } from '../../ducks/user.duck';

// ================ Async Thunks ================ //


/**
 * Fetch the full listing entities for all ids saved on the current user's profile.
 *
 * The ids come from currentUser.privateData.favorites so we must ensure that
 * entity is loaded first. loadData handles that sequencing.
 */
const fetchFavoriteListingsPayloadCreator = ({ favoriteIds, config }, thunkAPI) => {
  const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;

  if (!favoriteIds || favoriteIds.length === 0) {
    return Promise.resolve([]);
  }

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    ids: favoriteIds,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      return denormalisedResponseEntities(response);
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchFavoriteListingsThunk = createAsyncThunk(
  'FavoritesPage/fetchFavoriteListings',
  fetchFavoriteListingsPayloadCreator
);

// Backward compatible wrapper
export const fetchFavoriteListings = (favoriteIds, config) => (dispatch, getState, sdk) =>
  dispatch(fetchFavoriteListingsThunk({ favoriteIds, config })).unwrap();

// ================ Slice ================ //

const initialState = {
  listingIds: [],
  fetchInProgress: false,
  fetchError: null,
};

const favoritesPageSlice = createSlice({
  name: 'FavoritesPage',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchFavoriteListingsThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchFavoriteListingsThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.listingIds = (action.payload || []).map(l => l.id);
      })
      .addCase(fetchFavoriteListingsThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload;
      })
      .addCase(toggleFavoriteListingThunk.fulfilled, (state, action) => {
        const removedId = action.payload?.listingId;
        if (removedId) {
          state.listingIds = state.listingIds.filter(id => {
            const uuid = typeof id === 'string' ? id : id?.uuid;
            return uuid !== removedId;
          });
        }
      });
  },
});

export default favoritesPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  // First ensure currentUser (and their privateData.favorites) is loaded.
  return dispatch(fetchCurrentUser({ updateHasListings: false, updateNotifications: false })).then(
    () => {
      const currentUser = getState().user?.currentUser;
      const favoriteIds = getFavoriteIds(currentUser);
      return dispatch(fetchFavoriteListings(favoriteIds, config));
    }
  );
};
